import React, { useState } from "react";
import { Film, Type, Smile, Music, Volume2, VolumeX, Diamond } from "lucide-react";
import { Y, fmt, clamp } from "./constants";

const HEAD = 92;

const Handle = ({ side, onDown }) => (
  <span onPointerDown={onDown} className={`absolute top-0 bottom-0 w-2 cursor-ew-resize z-10 ${side === "l" ? "left-0 rounded-l" : "right-0 rounded-r"}`} style={{ background: Y }} data-testid={`handle-${side}`} />
);

const Wave = ({ peaks, from, to }) => {
  if (!peaks) return null;
  const a = Math.floor(from * peaks.length), b = Math.max(a + 1, Math.ceil(to * peaks.length));
  const seg = peaks.slice(a, b);
  return (
    <svg className="absolute inset-0 w-full h-full opacity-70" preserveAspectRatio="none" viewBox={`0 0 ${seg.length} 100`}>
      {seg.map((p, i) => <rect key={i} x={i} y={50 - p * 45} width={0.7} height={Math.max(2, p * 90)} fill="#93c5fd" />)}
    </svg>
  );
};

const Row = ({ icon: Icon, label, children, h = "h-8", testid, extra, width, onBg }) => (
  <div className="flex" data-testid={testid}>
    <div className={`shrink-0 ${h} flex items-center gap-1.5 px-2 text-[10px] text-zinc-400 border-r border-white/10 bg-[#161616] sticky left-0 z-20`} style={{ width: HEAD }}>
      <Icon className="h-3.5 w-3.5 shrink-0" /><span className="truncate flex-1">{label}</span>{extra}
    </div>
    <div className={`relative ${h}`} style={{ width }} onPointerDown={onBg}>{children}</div>
  </div>
);

export const Timeline = ({ clips, lay, overlays, audios, assets, total, gt, pps, sel, snap, mainMuted, onToggleMute,
  onSelect, onSeek, onClip, onReorder, onOverlay, onAudio }) => {
  const [ghost, setGhost] = useState(null); // {id, dx}
  const width = Math.max(total + 8, 20) * pps;

  const snapT = (t, selfId) => {
    if (!snap) return t;
    const pts = [0, gt, total];
    lay.forEach((l) => pts.push(l.start, l.start + l.len));
    overlays.forEach((o) => o.id !== selfId && pts.push(o.start, o.end));
    audios.forEach((a) => a.id !== selfId && pts.push(a.start));
    for (const p of pts) if (Math.abs(p - t) * pps < 8) return p;
    return t;
  };
  const drag = (e, kind, id, onMove, onEnd) => {
    e.stopPropagation(); e.preventDefault(); onSelect({ kind, id });
    const sx = e.clientX;
    const mm = (ev) => onMove((ev.clientX - sx) / pps);
    const up = (ev) => { window.removeEventListener("pointermove", mm); window.removeEventListener("pointerup", up); onEnd && onEnd((ev.clientX - sx) / pps); };
    window.addEventListener("pointermove", mm); window.addEventListener("pointerup", up);
  };

  const clipDown = (e, c, k, mode) => {
    const o = { ...c };
    if (mode === "move") drag(e, "clip", c.id, (d) => setGhost({ id: c.id, dx: d * pps }), (d) => {
      setGhost(null);
      if (Math.abs(d * pps) < 6) return;
      const center = lay[k].start + lay[k].len / 2 + d;
      const to = lay.filter((l, j) => j !== k && l.start + l.len / 2 < center).length;
      if (to !== k) onReorder(k, to);
    });
    else drag(e, "clip", c.id, (d) => {
      if (mode === "l") onClip(c.id, { in: clamp(o.in + d * o.speed, 0, o.out - 0.3) });
      else onClip(c.id, { out: clamp(o.out + d * o.speed, o.in + 0.3, o.srcDur || o.out + 999) });
    });
  };
  const ovDown = (e, ov, mode) => {
    const o = { ...ov }, L = o.end - o.start;
    drag(e, "overlay", ov.id, (d) => {
      if (mode === "move") { const s = Math.max(0, snapT(o.start + d, o.id)); onOverlay(o.id, { start: s, end: s + L }); }
      else if (mode === "l") onOverlay(o.id, { start: clamp(snapT(o.start + d, o.id), 0, o.end - 0.2) });
      else onOverlay(o.id, { end: Math.max(o.start + 0.2, snapT(o.end + d, o.id)) });
    });
  };
  const auDown = (e, au, mode) => {
    const o = { ...au };
    drag(e, "audio", au.id, (d) => {
      if (mode === "move") onAudio(o.id, { start: Math.max(0, snapT(o.start + d, o.id)) });
      else if (mode === "l") { const ni = clamp(o.in + d, 0, o.out - 0.2); onAudio(o.id, { in: ni, start: Math.max(0, o.start + (ni - o.in)) }); }
      else onAudio(o.id, { out: clamp(o.out + d, o.in + 0.2, o.srcDur || o.out + 999) });
    });
  };

  const subs = overlays.filter((o) => o.track === "sub");
  const rows = [...overlays.filter((o) => o.track !== "sub").map((o) => [o]).reverse(), ...(subs.length ? [subs] : [])];
  const tickStep = pps > 80 ? 1 : pps > 30 ? 2 : pps > 15 ? 5 : 10;
  const isSel = (kind, id) => sel?.kind === kind && sel?.id === id;
  const seekAt = (e) => { const r = e.currentTarget.getBoundingClientRect(); onSeek(Math.max(0, (e.clientX - r.left) / pps)); };

  const bg = (e) => { if (e.target === e.currentTarget) { onSelect(null); seekAt(e); } };

  return (
    <div className="relative overflow-x-auto overflow-y-auto max-h-[260px]" data-testid="timeline">
      <div style={{ width: width + HEAD }} className="relative pb-2">
        {/* ruler */}
        <div className="flex sticky top-0 z-30 bg-[#141414]">
          <div className="shrink-0 border-r border-white/10 bg-[#161616] sticky left-0 z-40" style={{ width: HEAD }} />
          <div className="relative h-6 cursor-pointer" style={{ width }} data-testid="timeline-ruler"
            onPointerDown={(e) => { seekAt(e); const r = e.currentTarget.getBoundingClientRect(); const mm = (ev) => onSeek(Math.max(0, (ev.clientX - r.left) / pps)); const up = () => { window.removeEventListener("pointermove", mm); window.removeEventListener("pointerup", up); }; window.addEventListener("pointermove", mm); window.addEventListener("pointerup", up); }}>
            {Array.from({ length: Math.ceil(width / pps / tickStep) + 1 }).map((_, i) => (
              <span key={i} className="absolute top-0 h-full border-l border-white/15 pl-1 text-[9px] text-zinc-500 tabular-nums pointer-events-none" style={{ left: i * tickStep * pps }}>{fmt(i * tickStep)}</span>
            ))}
          </div>
        </div>

        <div className="space-y-1 mt-1">
          {rows.map((group) => (
            <Row key={group[0].id} icon={group[0].type === "text" ? Type : Smile} label={group[0].track === "sub" ? "Subtítulos" : group[0].type === "text" ? "Texto" : "Sticker"} testid="track-overlay" width={width} onBg={bg}>
              {group.map((o) => (
                <div key={o.id} onPointerDown={(e) => ovDown(e, o, "move")} data-testid={`tl-ov-${o.id}`}
                  className="absolute top-0.5 bottom-0.5 rounded px-2.5 text-[10px] font-semibold truncate cursor-grab flex items-center"
                  style={{ left: o.start * pps, width: Math.max(6, (o.end - o.start) * pps), background: o.type === "text" ? "rgba(255,214,10,.22)" : "rgba(244,114,182,.25)", color: o.type === "text" ? Y : "#f9a8d4", outline: isSel("overlay", o.id) ? `2px solid ${Y}` : "1px solid rgba(255,255,255,.12)" }}>
                  {o.type === "text" ? o.text.replace(/\n/g, " ") : "Sticker"}
                  {isSel("overlay", o.id) && <><Handle side="l" onDown={(e) => ovDown(e, o, "l")} /><Handle side="r" onDown={(e) => ovDown(e, o, "r")} /></>}
                </div>
              ))}
            </Row>
          ))}

          <Row icon={Film} label="Vídeo" h="h-14" testid="track-video" width={width} onBg={bg}
            extra={<button onClick={onToggleMute} data-testid="mute-main" className="text-zinc-400 hover:text-white">{mainMuted ? <VolumeX className="h-3.5 w-3.5 text-red-400" /> : <Volume2 className="h-3.5 w-3.5" />}</button>}>
            {!clips.length && <span className="absolute left-3 top-4 text-[10px] text-zinc-600 pointer-events-none">Pista principal — importa un clip</span>}
            {clips.map((c, k) => (
              <div key={c.id} onPointerDown={(e) => clipDown(e, c, k, "move")} data-testid={`tl-clip-${k}`}
                className="absolute top-0.5 bottom-0.5 rounded-md overflow-hidden cursor-grab bg-[#2a2a2a]"
                style={{ left: lay[k].start * pps + (ghost?.id === c.id ? ghost.dx : 0), width: Math.max(8, lay[k].len * pps), outline: isSel("clip", c.id) ? `2px solid ${Y}` : "1px solid rgba(255,255,255,.15)", zIndex: ghost?.id === c.id ? 15 : isSel("clip", c.id) ? 5 : 1, opacity: ghost?.id === c.id ? 0.8 : 1 }}>
                <video src={`${c.url}#t=${c.in + 0.1}`} muted preload="metadata" className="absolute inset-0 w-full h-full object-cover opacity-50 pointer-events-none" />
                <span className="absolute left-2.5 top-1 text-[10px] bg-black/60 px-1.5 rounded truncate max-w-[85%]">{c.name}{c.speed !== 1 ? ` · ${c.speed}x` : ""}</span>
                <span className="absolute left-2.5 bottom-1 text-[9px] text-zinc-300 bg-black/50 px-1 rounded tabular-nums">{fmt(lay[k].len)}</span>
                {isSel("clip", c.id) && <><Handle side="l" onDown={(e) => clipDown(e, c, k, "l")} /><Handle side="r" onDown={(e) => clipDown(e, c, k, "r")} /></>}
              </div>
            ))}
            {clips.map((c, k) => k > 0 && (
              <button key={"tr" + c.id} onPointerDown={(e) => { e.stopPropagation(); onSelect({ kind: "clip", id: c.id, tab: "transition" }); }} data-testid={`tl-transition-${k}`}
                title="Transición" className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-6 w-6 rounded-md grid place-items-center z-20 border border-black"
                style={{ left: (lay[k].start + lay[k].td / 2) * pps, background: c.transition?.type !== "none" ? Y : "#3f3f46" }}>
                <Diamond className={`h-3 w-3 ${c.transition?.type !== "none" ? "text-black fill-black" : "text-zinc-300"}`} />
              </button>
            ))}
          </Row>

          {audios.map((a) => (
            <Row key={a.id} icon={Music} label="Audio" h="h-9" testid="track-audio" width={width} onBg={bg}>
              <div onPointerDown={(e) => auDown(e, a, "move")} data-testid={`tl-audio-${a.id}`}
                className="absolute top-0.5 bottom-0.5 rounded overflow-hidden cursor-grab bg-blue-500/20"
                style={{ left: a.start * pps, width: Math.max(8, (a.out - a.in) * pps), outline: isSel("audio", a.id) ? `2px solid ${Y}` : "1px solid rgba(147,197,253,.3)" }}>
                <Wave peaks={assets[a.mediaId]?.peaks} from={a.in / (a.srcDur || 1)} to={a.out / (a.srcDur || 1)} />
                <span className="absolute left-2.5 top-0.5 text-[10px] text-blue-200 truncate max-w-[85%] bg-black/40 px-1 rounded">{a.name}</span>
                {isSel("audio", a.id) && <><Handle side="l" onDown={(e) => auDown(e, a, "l")} /><Handle side="r" onDown={(e) => auDown(e, a, "r")} /></>}
              </div>
            </Row>
          ))}
        </div>

        {/* playhead */}
        <div className="absolute top-0 bottom-0 w-0.5 pointer-events-none z-30" style={{ left: HEAD + gt * pps, background: Y }} data-testid="playhead">
          <div className="absolute -top-0 -left-[5px] h-0 w-0" style={{ borderLeft: "6px solid transparent", borderRight: "6px solid transparent", borderTop: `8px solid ${Y}` }} />
        </div>
      </div>
    </div>
  );
};
